import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../content/data';
import { Reveal } from './motion';
import { ArrowBtn, mediaUrl } from './bits';

export const PAGE_OPTIONS = [
  ['home', 'Home'], ['about', 'About'], ['students', 'For Students'], ['programmes', 'Programmes'],
  ['flagship', '18-Month Flagship'], ['technical', 'Technical Track'], ['business-creator', 'Business & Creator'],
  ['scholarship', 'Scholarship'], ['enterprises', 'Enterprises'], ['csr', 'CSR Routing'], ['talent', 'Talent'],
  ['marketing-cell', 'Marketing Cell'], ['research', 'Research'], ['membership', 'Membership'],
  ['institutions', 'Institutions'], ['press', 'Press & Insights'], ['contact', 'Contact'],
];

const PATH_TO_PAGE = {
  '/': 'home', '/about': 'about', '/students': 'students', '/programmes': 'programmes',
  '/programmes/flagship': 'flagship', '/programmes/technical': 'technical', '/programmes/business-creator': 'business-creator',
  '/scholarship': 'scholarship', '/enterprises': 'enterprises', '/enterprises/csr': 'csr', '/enterprises/talent': 'talent',
  '/enterprises/marketing-cell': 'marketing-cell', '/enterprises/research': 'research', '/enterprises/membership': 'membership',
  '/institutions': 'institutions', '/press': 'press', '/contact': 'contact',
};

function SectionBlock({ s }) {
  const dark = s.theme === 'dark';
  const gold = s.theme === 'gold';
  const bgCls = dark ? 'bg-grid-dark bg-navy text-white' : gold ? 'bg-gold text-navy' : 'bg-white text-navy';
  const tagCls = dark ? 'text-gold' : gold ? 'text-navy' : 'text-electric';
  const bodyCls = dark ? 'text-white/70' : gold ? 'text-navy/80' : 'text-navy/70';
  const chipCls = dark ? 'border-gold/25 bg-navy-800 text-gold' : gold ? 'border-navy/15 bg-white/60 text-navy' : 'border-navy/15 bg-paper text-navy';
  return (
    <section data-testid={`custom-section-${s.id.slice(0, 8)}`} className={`${bgCls} py-16 sm:py-24`}>
      <div className="wrap">
        {s.tag && <Reveal><span className={`overline ${tagCls}`}>{s.tag}</span></Reveal>}
        <div className={`grid gap-10 ${s.image ? 'lg:grid-cols-2 lg:items-center' : ''}`}>
          <div>
            <Reveal delay={0.05}><h2 className="text-h2 mt-4 font-semibold">{s.heading}</h2></Reveal>
            {s.body && <Reveal delay={0.1}><p className={`mt-5 max-w-2xl text-base leading-relaxed sm:text-lg ${bodyCls}`}>{s.body}</p></Reveal>}
            {s.bullets?.length > 0 && (
              <Reveal delay={0.15}>
                <div className="mt-7 flex flex-wrap gap-2">
                  {s.bullets.map((b, i) => (
                    <span key={i} className={`rounded-full border px-4 py-2 text-xs font-semibold sm:text-sm ${chipCls}`}>{b}</span>
                  ))}
                </div>
              </Reveal>
            )}
            {s.ctaLabel && s.ctaUrl && (
              <Reveal delay={0.2} className="mt-8">
                {/^https?:\/\//.test(s.ctaUrl) ? (
                  <a href={s.ctaUrl} target="_blank" rel="noreferrer" className={`inline-flex touch-target items-center rounded-full px-7 py-3.5 text-sm font-semibold transition-transform hover:-translate-y-0.5 ${dark ? 'bg-gold text-navy' : 'bg-electric text-white'}`}>
                    {s.ctaLabel} →
                  </a>
                ) : (
                  <ArrowBtn to={s.ctaUrl} variant={dark ? 'gold' : 'primary'} testId={`custom-cta-${s.id.slice(0, 8)}`}>{s.ctaLabel}</ArrowBtn>
                )}
              </Reveal>
            )}
          </div>
          {s.image && (
            <Reveal delay={0.1}>
              <div className={`overflow-hidden rounded-3xl border shadow-2xl ${dark ? 'border-white/10 shadow-black/40' : 'border-navy/10 shadow-navy/15'}`}>
                <img src={mediaUrl(s.image)} alt={s.heading} className="aspect-[4/3] w-full object-cover" loading="lazy" />
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

export default function CustomSections() {
  const { pathname } = useLocation();
  const page = PATH_TO_PAGE[pathname];
  const [sections, setSections] = useState([]);
  const [slots, setSlots] = useState({});

  useEffect(() => {
    if (!page) { setSections([]); return; }
    axios.get(`${API}/sections?page=${page}`).then((r) => setSections(r.data)).catch(() => setSections([]));
  }, [page]);

  useEffect(() => {
    const t = setTimeout(() => {
      const main = document.querySelector('main');
      const scope = main?.querySelector('[data-testid$="-page"]');
      if (!scope) return;
      const mk = (afterEl, pos) => {
        if (!afterEl) return null;
        const d = document.createElement('div');
        d.setAttribute('data-section-slot', pos);
        afterEl.after(d);
        return d;
      };
      const next = {};
      const hero = scope.querySelector(':scope > header, :scope > section');
      if (hero) next.afterHero = mk(hero, 'afterHero');
      const secs = scope.querySelectorAll(':scope > section');
      if (secs.length >= 3) next.midPage = mk(secs[Math.floor(secs.length / 2)], 'midPage');
      setSlots(next);
    }, 600);
    return () => {
      clearTimeout(t);
      document.querySelectorAll('[data-section-slot]').forEach((n) => n.remove());
      setSlots({});
    };
  }, [pathname, sections]);

  if (!sections.length) return null;
  const by = (p) => sections.filter((s) => (s.placement || 'beforeFooter') === p);
  return (
    <>
      {by('beforeFooter').map((s) => <SectionBlock key={s.id} s={s} />)}
      {slots.afterHero && by('afterHero').length > 0 && createPortal(by('afterHero').map((s) => <SectionBlock key={s.id} s={s} />), slots.afterHero)}
      {slots.midPage && by('midPage').length > 0 && createPortal(by('midPage').map((s) => <SectionBlock key={s.id} s={s} />), slots.midPage)}
    </>
  );
}
